import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    CircleAlert,
    ClipboardList,
    Clock3,
    LoaderCircle,
    RefreshCw,
    ShieldCheck,
    ShieldX,
    WalletCards,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaCircleUser } from 'react-icons/fa6'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './FnFSettlementDetailScreen.module.css'

const STATUS_CONFIG = {
    DRAFT: {
        label: 'Draft',
        color: '#E65100',
        bg: '#FFF3E0',
        description: 'Settlement has been generated and is awaiting staff confirmation.',
    },
    ACCEPTED: {
        label: 'Accepted',
        color: '#1565C0',
        bg: '#E3F2FD',
        description: 'Staff has accepted the settlement. Payment is still pending.',
    },
    PAID: {
        label: 'Paid',
        color: '#2E7D32',
        bg: '#E8F5E9',
        description: 'Settlement is complete and the staff stays excluded from payroll.',
    },
    CANCELLED: {
        label: 'Cancelled',
        color: '#546E7A',
        bg: '#ECEFF1',
        description: 'Settlement is cancelled and the staff returns to the regular payroll flow.',
    },
}

const ACTION_DEFAULT_NOTE = {
    ACCEPTED: 'Owner confirmed acceptance.',
    PAID: 'Settlement paid to staff.',
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount, minimumFractionDigits = 2, maximumFractionDigits = 2) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits,
        maximumFractionDigits,
    })
}

const formatDateReadable = (dateValue) => {
    if (!dateValue) return '—'
    const normalized = String(dateValue).includes('T') ? String(dateValue) : `${dateValue}T00:00:00`
    const date = new Date(normalized)
    if (Number.isNaN(date.getTime())) return dateValue
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    })
}

const formatDateTime = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const resolveSettlementFromPayload = (payload, settlementIdentifier) => {
    if (!payload) return null

    if (payload?.settlementIdentifier) {
        return payload
    }

    const settlements = Array.isArray(payload?.settlements) ? payload.settlements : []
    if (!settlementIdentifier) return settlements[0] || null

    return (
        settlements.find((entry) => entry?.settlementIdentifier === settlementIdentifier) || settlements[0] || null
    )
}

const getActionsForStatus = (status) => {
    switch (String(status || '').toUpperCase()) {
        case 'DRAFT':
            return [
                {
                    key: 'ACCEPTED',
                    label: 'Mark as Accepted',
                    tone: 'primary',
                    icon: CheckCircle2,
                    helper: 'Confirms the amount with staff and keeps payment pending.',
                },
                {
                    key: 'CANCELLED',
                    label: 'Cancel Settlement',
                    tone: 'destructive',
                    icon: ShieldX,
                    helper: 'Use when the staff rejoins payroll or the draft must be invalidated.',
                },
            ]
        case 'ACCEPTED':
            return [
                {
                    key: 'PAID',
                    label: 'Mark as Paid',
                    tone: 'primary',
                    icon: BadgeIndianRupee,
                    helper: 'Completes the FnF payout and finalizes the settlement.',
                },
                {
                    key: 'CANCELLED',
                    label: 'Cancel — Staff Extended',
                    tone: 'destructive',
                    icon: ShieldX,
                    helper: 'Cancels the settlement and puts the staff back into regular payroll.',
                },
            ]
        default:
            return []
    }
}

const FnFSettlementDetailScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { staffIdentifier: routeStaffIdentifier, settlementIdentifier } = useParams()

    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const seededSettlement = location?.state?.settlement || location?.state?.fnfResult || null
    const seededStaffIdentifier = location?.state?.staffIdentifier || routeStaffIdentifier || ''
    const seededStaffName = location?.state?.staffName || seededSettlement?.staffName || 'Staff Member'
    const seededDesignation =
        location?.state?.designationLabel || seededSettlement?.designationLabel || seededSettlement?.designation || '—'

    const [settlement, setSettlement] = useState(seededSettlement)
    const [staffName, setStaffName] = useState(seededStaffName)
    const [designationLabel, setDesignationLabel] = useState(seededDesignation)
    const [isLoading, setIsLoading] = useState(!seededSettlement)
    const [isUpdating, setIsUpdating] = useState(false)
    const [noteDraft, setNoteDraft] = useState('')
    const [noteError, setNoteError] = useState('')
    const [loadingAction, setLoadingAction] = useState('')

    const statusKey = String(settlement?.settlementStatus || '').toUpperCase()
    const statusMeta = STATUS_CONFIG[statusKey] || {
        label: settlement?.settlementStatus || '—',
        color: '#475569',
        bg: '#E2E8F0',
        description: 'Settlement status unavailable.',
    }

    const actionList = useMemo(() => getActionsForStatus(statusKey), [statusKey])

    const fetchSettlementDetail = useCallback(
        async ({ silent = false } = {}) => {
            if (!authCtx?.token || !activeStore?.storeIdentifier || !seededStaffIdentifier || !settlementIdentifier) {
                setIsLoading(false)
                return null
            }

            if (!silent) setIsLoading(true)

            try {
                const response = await fetch(BASE_URL_API + 'getFnFSettlement', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                        staffIdentifier: seededStaffIdentifier,
                        settlementIdentifier,
                    }),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    const payload = respo?.response || null
                    const nextSettlement = resolveSettlementFromPayload(payload, settlementIdentifier)

                    setSettlement(nextSettlement)
                    setStaffName(payload?.staffName || nextSettlement?.staffName || seededStaffName)
                    setDesignationLabel(
                        payload?.designationLabel ||
                            nextSettlement?.designationLabel ||
                            nextSettlement?.designation ||
                            seededDesignation
                    )
                    return nextSettlement
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return null
                }

                toast.error(respo?.message || 'Failed to load settlement detail.')
                return null
            } catch {
                toast.error('Network error.')
                return null
            } finally {
                setIsLoading(false)
            }
        },
        [
            activeStore?.storeIdentifier,
            authCtx,
            seededDesignation,
            seededStaffIdentifier,
            seededStaffName,
            settlementIdentifier,
        ]
    )

    useEffect(() => {
        if (!seededSettlement) {
            fetchSettlementDetail()
            return
        }

        setSettlement(seededSettlement)
        setStaffName(seededStaffName)
        setDesignationLabel(seededDesignation)
        setIsLoading(false)
    }, [fetchSettlementDetail, seededDesignation, seededSettlement, seededStaffName])

    const updateFnFStatus = useCallback(
        async (targetStatus, fallbackNote = '') => {
            if (!settlement?.settlementIdentifier) {
                toast.error('Settlement context is missing.')
                return
            }

            const resolvedNote = noteDraft.trim() || fallbackNote.trim()

            if (targetStatus === 'CANCELLED' && !resolvedNote) {
                setNoteError('A cancellation note is required.')
                return
            }

            setNoteError('')

            try {
                setIsUpdating(true)
                setLoadingAction(targetStatus)

                const response = await fetch(BASE_URL_API + 'updateFnFSettlementStatus', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx?.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                        settlementIdentifier: settlement.settlementIdentifier,
                        targetStatus,
                        updatedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                        note: resolvedNote,
                    }),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setSettlement((prev) => ({
                        ...prev,
                        ...respo?.response,
                        settlementStatus: respo?.response?.newStatus || prev?.settlementStatus,
                        acceptedOn: respo?.response?.acceptedOn ?? prev?.acceptedOn,
                        paidOn: respo?.response?.paidOn ?? prev?.paidOn,
                        paidBy: respo?.response?.paidBy ?? prev?.paidBy,
                        note: respo?.response?.note ?? resolvedNote,
                        remarks: respo?.response?.remarks ?? prev?.remarks,
                    }))

                    setNoteDraft('')
                    toast.success(respo?.response?.payrollImpact || respo?.message || 'Settlement updated.')
                    await fetchSettlementDetail({ silent: true })
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                if (respo?.errorCode === 'SYS_002') {
                    toast.error(respo?.message || 'Another update was detected. Refreshing latest detail...')
                    await fetchSettlementDetail({ silent: true })
                    return
                }

                if (respo?.errorCode === 'PAY_002' && respo?.response?.currentStatus) {
                    setSettlement((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  settlementStatus: respo.response.currentStatus,
                              }
                            : prev
                    )
                }

                if (targetStatus === 'CANCELLED' && respo?.errorCode === 'VAL_001') {
                    setNoteError(respo?.message || 'A cancellation note is required.')
                } else {
                    toast.error(respo?.message || 'Update failed.')
                }
            } catch {
                toast.error('Network error.')
            } finally {
                setIsUpdating(false)
                setLoadingAction('')
            }
        },
        [activeStore?.storeIdentifier, authCtx, fetchSettlementDetail, noteDraft, settlement?.settlementIdentifier]
    )

    const timelineItems = useMemo(
        () => [
            {
                label: 'Generated',
                value: formatDateTime(settlement?.computedOn),
                helper: settlement?.cycleLabel || 'Draft created',
            },
            {
                label: 'Accepted On',
                value: formatDateReadable(settlement?.acceptedOn),
                helper: statusKey === 'DRAFT' ? 'Pending staff acceptance' : 'Acceptance recorded',
            },
            {
                label: 'Paid On',
                value: formatDateReadable(settlement?.paidOn),
                helper: statusKey === 'PAID' ? `Paid by ${settlement?.paidBy || '—'}` : 'Pending final payout',
            },
            {
                label: 'Updated On',
                value: formatDateTime(settlement?.updatedOn),
                helper: settlement?.payrollImpact || 'Latest workflow update',
            },
        ],
        [
            settlement?.acceptedOn,
            settlement?.computedOn,
            settlement?.cycleLabel,
            settlement?.paidBy,
            settlement?.paidOn,
            settlement?.payrollImpact,
            settlement?.updatedOn,
            statusKey,
        ]
    )

    const notePreview = settlement?.remarks || settlement?.note || 'No remarks recorded for this settlement yet.'

    if (!seededStaffIdentifier || !settlementIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.inlineAlertWarn}>
                    <AlertTriangle size={16} />
                    <span>Settlement identifier is missing. Open this screen from FnF History or a generated FnF card.</span>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>

                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>FnF Settlement Detail</h1>
                    <p className={styles.headerSub}>{staffName}</p>
                </div>

                <span className={styles.statusPill} style={{ color: statusMeta.color, backgroundColor: statusMeta.bg }}>
                    {statusMeta.label}
                </span>
            </div>

            {isLoading ? (
                <div className={styles.loaderCard}>
                    <LoaderCircle size={20} className={styles.spinIcon} />
                    <span>Loading FnF settlement detail...</span>
                </div>
            ) : !settlement ? (
                <div className={styles.inlineAlertWarn}>
                    <CircleAlert size={16} />
                    <span>Settlement record not found for this staff member.</span>
                </div>
            ) : (
                <>
                    <section className={styles.heroCard}>
                        <div className={styles.identityBlock}>
                            <FaCircleUser className={styles.avatarIcon} />
                            <div>
                                <strong>{staffName}</strong>
                                <span>{designationLabel}</span>
                            </div>
                        </div>

                        <div className={styles.heroMetaGrid}>
                            <div className={styles.heroMetaCell}>
                                <span>Settlement ID</span>
                                <strong>{settlement?.settlementIdentifier || '—'}</strong>
                            </div>
                            <div className={styles.heroMetaCell}>
                                <span>Cycle</span>
                                <strong>{settlement?.cycleLabel || '—'}</strong>
                            </div>
                            <div className={styles.heroMetaCell}>
                                <span>Last Working Date</span>
                                <strong>{formatDateReadable(settlement?.lastWorkingDate)}</strong>
                            </div>
                            <div className={styles.heroMetaCell}>
                                <span>Computed On</span>
                                <strong>{formatDateTime(settlement?.computedOn)}</strong>
                            </div>
                        </div>

                        <div className={styles.netBanner}>
                            <BadgeIndianRupee size={18} />
                            <div>
                                <span>Net Settlement Amount</span>
                                <strong>₹{formatINR(settlement?.netSettlementAmount, 2, 2)}</strong>
                            </div>
                        </div>

                        <p className={styles.statusDescription}>{statusMeta.description}</p>
                    </section>

                    <section className={styles.summaryGrid}>
                        <div className={styles.summaryCard}>
                            <div className={styles.cardHead}>
                                <WalletCards size={15} />
                                <strong>Earnings Snapshot</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Earned Salary</span>
                                <strong>₹{formatINR(settlement?.earnings?.earnedSalary, 2, 2)}</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Commission</span>
                                <strong>₹{formatINR(settlement?.earnings?.earnedIncentive, 2, 2)}</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>PL Encashment</span>
                                <strong>₹{formatINR(settlement?.earnings?.leaveEncashmentAmount, 2, 2)}</strong>
                            </div>
                            <div className={styles.totalLine}>
                                <span>Total Earnings</span>
                                <strong>₹{formatINR(settlement?.earnings?.totalEarnings, 2, 2)}</strong>
                            </div>
                        </div>

                        <div className={styles.summaryCard}>
                            <div className={styles.cardHead}>
                                <ShieldCheck size={15} />
                                <strong>Deductions Snapshot</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Advance Deducted</span>
                                <strong>₹{formatINR(settlement?.deductions?.advanceDeducted, 2, 2)}</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Statutory</span>
                                <strong>₹{formatINR(settlement?.deductions?.statutoryDeductions, 2, 2)}</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Total Working Days</span>
                                <strong>{formatINR(settlement?.attendance?.totalWorkingDaysInCycle || 0, 0, 0)}</strong>
                            </div>
                            <div className={styles.totalLine}>
                                <span>Total Deductions</span>
                                <strong>₹{formatINR(settlement?.deductions?.totalDeductions, 2, 2)}</strong>
                            </div>
                        </div>

                        <div className={styles.summaryCard}>
                            <div className={styles.cardHead}>
                                <CalendarDays size={15} />
                                <strong>Lifecycle &amp; Payroll Impact</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Accepted On</span>
                                <strong>{formatDateReadable(settlement?.acceptedOn)}</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Paid On</span>
                                <strong>{formatDateReadable(settlement?.paidOn)}</strong>
                            </div>
                            <div className={styles.rowLine}>
                                <span>Paid By</span>
                                <strong>{settlement?.paidBy || '—'}</strong>
                            </div>
                            <p className={styles.impactText}>{settlement?.payrollImpact || 'Payroll impact will appear after the next status update.'}</p>
                        </div>
                    </section>

                    <section className={styles.contentGrid}>
                        <div className={styles.panelCard}>
                            <div className={styles.cardHead}>
                                <Clock3 size={15} />
                                <strong>Settlement Timeline</strong>
                            </div>

                            <div className={styles.timelineList}>
                                {timelineItems.map((item) => (
                                    <div key={item.label} className={styles.timelineRow}>
                                        <span className={styles.timelineDot} />
                                        <div>
                                            <strong>{item.label}</strong>
                                            <p>{item.value}</p>
                                            <small>{item.helper}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.panelCard}>
                            <div className={styles.cardHead}>
                                <ClipboardList size={15} />
                                <strong>Remarks &amp; Notes</strong>
                            </div>

                            <div className={styles.notePreviewBox}>{notePreview}</div>

                            {actionList.length > 0 ? (
                                <>
                                    <label className={styles.noteLabel} htmlFor="fnf-status-note">
                                        Status note {statusKey === 'CANCELLED' ? '*' : '(optional)'}
                                    </label>
                                    <textarea
                                        id="fnf-status-note"
                                        rows={4}
                                        className={styles.noteInput}
                                        value={noteDraft}
                                        onChange={(event) => setNoteDraft(event.target.value)}
                                        placeholder={
                                            statusKey === 'DRAFT'
                                                ? 'e.g. Staff accepted the settlement amount verbally.'
                                                : 'e.g. Paid via bank transfer on May 10 or reason for cancellation.'
                                        }
                                    />
                                    {noteError ? (
                                        <div className={styles.inlineAlertError}>
                                            <AlertTriangle size={14} />
                                            <span>{noteError}</span>
                                        </div>
                                    ) : null}

                                    <div className={styles.actionStack}>
                                        {actionList.map((action) => {
                                            const Icon = action.icon
                                            const isBusy = isUpdating && loadingAction === action.key
                                            const buttonClassName =
                                                action.tone === 'destructive' ? styles.destructiveBtn : styles.primaryBtn

                                            return (
                                                <button
                                                    key={action.key}
                                                    type="button"
                                                    className={buttonClassName}
                                                    disabled={isUpdating}
                                                    onClick={() => updateFnFStatus(action.key, ACTION_DEFAULT_NOTE[action.key] || '')}
                                                >
                                                    {isBusy ? (
                                                        <>
                                                            <LoaderCircle size={14} className={styles.spinIcon} /> Updating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Icon size={14} /> {action.label}
                                                            {action.tone === 'primary' ? <ArrowRight size={14} /> : null}
                                                        </>
                                                    )}
                                                    <span className={styles.actionHelper}>{action.helper}</span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.terminalStateBox}>
                                    <CheckCircle2 size={16} />
                                    <div>
                                        <strong>No further actions available</strong>
                                        <span>This settlement is in a terminal state.</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className={styles.footerActions}>
                        <button type="button" className={styles.secondaryBtn} onClick={() => fetchSettlementDetail()} disabled={isLoading || isUpdating}>
                            <RefreshCw size={14} /> Refresh Detail
                        </button>

                        {statusKey === 'CANCELLED' ? (
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                onClick={() =>
                                    navigate(`/payroll/fnf/${seededStaffIdentifier}`, {
                                        state: {
                                            staffIdentifier: seededStaffIdentifier,
                                            staffName,
                                            activeTab: 'GENERATE',
                                        },
                                    })
                                }
                            >
                                Re-generate FnF <ArrowRight size={14} />
                            </button>
                        ) : null}
                    </section>
                </>
            )}
        </div>
    )
}

export default FnFSettlementDetailScreen